import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { cn } from '@/lib/utils/cn'
import { CLOSURE_TYPE_CATALOG, type VenueClosureCreateInput, type VenueClosureType } from '@/lib/venue/closureTypes'
import {
  CLOSURE_WEEKDAY_CHIPS,
  emptyClosureFormState,
  hasClosureFormErrors,
  switchClosureFormType,
  toClosureCreateInput,
  toggleClosureCourt,
  toggleClosureWeekday,
  validateClosureForm,
  type ClosureFormErrors,
} from '@/lib/venue/closureViewModel'
import { TIME_OF_DAY_OPTIONS } from '@/lib/venue/operatingHourTypes'

function FieldError({ message }: { message: string | undefined }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-danger-500">{message}</p>
}

const REASON_REQUIRED_TYPES: VenueClosureType[] = ['BLOCK', 'RECURRING_BLOCK']

/**
 * 휴무·차단·레슨 등록 폼(요구사항 등록 폼 4종, 설계 §2). 유형 탭으로 하나의 모달에서 전환하며,
 * 유형별로 표시되는 입력 필드만 다르다(`CourtFormModal.tsx` 단일 모달 패턴 재사용).
 *
 * 🚨 여기서 서버로 보내지 않는다 — 클라이언트 검증을 통과하면 [onValidated] 로 조립된 입력을
 * 넘기고, 실제 등록·확정 예약 겹침 확인은 `ClosureCreateConfirmModal` 이 담당한다(설계 §2).
 */
export function ClosureFormModal({
  courts,
  onValidated,
  onClose,
}: {
  courts: { courtId: number; name: string }[]
  onValidated: (input: Omit<VenueClosureCreateInput, 'force'>) => void
  onClose: () => void
}) {
  const [form, setForm] = useState(() => emptyClosureFormState())
  const [errors, setErrors] = useState<ClosureFormErrors>({})

  function updateForm<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  function handleTypeChange(type: VenueClosureType) {
    setForm((current) => switchClosureFormType(current, type))
    setErrors({})
  }

  function handleSubmit() {
    const validationErrors = validateClosureForm(form)
    if (hasClosureFormErrors(validationErrors)) {
      setErrors(validationErrors)
      return
    }
    setErrors({})
    onValidated(toClosureCreateInput(form))
  }

  const showDateRange = form.type === 'HOLIDAY'
  const showSingleDate = form.type === 'BLOCK'
  const showWeekdays = form.type === 'RECURRING_BLOCK' || form.type === 'LESSON'
  const showTimeRange = form.type !== 'HOLIDAY'
  const showTitle = form.type === 'LESSON'
  const reasonRequired = REASON_REQUIRED_TYPES.includes(form.type)

  return (
    <Modal title="휴무·차단·레슨 추가" onClose={onClose}>
      <div className="space-y-4">
        <div role="tablist" aria-label="유형" className="flex flex-wrap gap-2">
          {CLOSURE_TYPE_CATALOG.map((option) => {
            const selected = form.type === option.value
            return (
              <button
                key={option.value}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => {
                  handleTypeChange(option.value)
                }}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                  selected
                    ? 'border-brand-500 bg-brand-50 text-brand-500'
                    : 'border-slateish-300 text-slateish-600 hover:bg-slateish-50'
                )}
              >
                {option.label}
              </button>
            )
          })}
        </div>

        {showDateRange ? (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="closure-start-date">
                시작일
              </label>
              <Input
                id="closure-start-date"
                type="date"
                value={form.date}
                aria-invalid={Boolean(errors.date)}
                onChange={(event) => {
                  updateForm('date', event.target.value)
                }}
              />
              <FieldError message={errors.date} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="closure-end-date">
                종료일
              </label>
              <Input
                id="closure-end-date"
                type="date"
                value={form.endDate}
                placeholder="하루만 쉬면 시작일과 같게"
                aria-invalid={Boolean(errors.endDate)}
                onChange={(event) => {
                  updateForm('endDate', event.target.value)
                }}
              />
              <FieldError message={errors.endDate} />
            </div>
          </div>
        ) : null}

        {showSingleDate ? (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="closure-date">
              날짜
            </label>
            <Input
              id="closure-date"
              type="date"
              value={form.date}
              aria-invalid={Boolean(errors.date)}
              onChange={(event) => {
                updateForm('date', event.target.value)
              }}
            />
            <FieldError message={errors.date} />
          </div>
        ) : null}

        {showWeekdays ? (
          <div>
            <span className="mb-1.5 block text-sm font-medium text-ink-700">반복 요일</span>
            <div className="flex flex-wrap gap-2">
              {CLOSURE_WEEKDAY_CHIPS.map((chip) => {
                const selected = form.recurringDayOfWeeks.includes(chip.value)
                return (
                  <button
                    key={chip.value}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => {
                      setForm((current) => toggleClosureWeekday(current, chip.value))
                    }}
                    className={cn(
                      'h-9 w-9 rounded-full border text-sm font-medium transition-colors',
                      selected
                        ? 'border-brand-500 bg-brand-500 text-white'
                        : 'border-slateish-300 text-slateish-600 hover:bg-slateish-50'
                    )}
                  >
                    {chip.label}
                  </button>
                )
              })}
            </div>
            <FieldError message={errors.recurringDayOfWeeks} />
          </div>
        ) : null}

        {showTimeRange ? (
          <div>
            <span className="mb-1.5 block text-sm font-medium text-ink-700">시간대</span>
            <div className="flex items-center gap-2">
              <Select
                aria-label="시작 시각"
                value={form.startTime}
                onChange={(event) => {
                  updateForm('startTime', event.target.value)
                }}
              >
                {TIME_OF_DAY_OPTIONS.map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </Select>
              <span className="text-slateish-400">~</span>
              <Select
                aria-label="종료 시각"
                value={form.endTime}
                onChange={(event) => {
                  updateForm('endTime', event.target.value)
                }}
              >
                {TIME_OF_DAY_OPTIONS.map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </Select>
            </div>
            <FieldError message={errors.time} />
          </div>
        ) : null}

        {showTitle ? (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="closure-title">
              레슨 이름
            </label>
            <Input
              id="closure-title"
              value={form.title}
              placeholder="예) 성인 레슨 A반"
              aria-invalid={Boolean(errors.title)}
              onChange={(event) => {
                updateForm('title', event.target.value)
              }}
            />
            <FieldError message={errors.title} />
          </div>
        ) : null}

        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="closure-apply-scope">
            적용 코트
          </label>
          <Select
            id="closure-apply-scope"
            value={form.applyScope}
            onChange={(event) => {
              updateForm('applyScope', event.target.value as typeof form.applyScope)
            }}
          >
            <option value="ALL_COURTS">전체 코트</option>
            <option value="SELECTED_COURTS">코트 선택</option>
          </Select>
          {form.applyScope === 'SELECTED_COURTS' ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {courts.map((court) => {
                const selected = form.courtIds.includes(court.courtId)
                return (
                  <button
                    key={court.courtId}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => {
                      setForm((current) => toggleClosureCourt(current, court.courtId))
                    }}
                    className={cn(
                      'rounded-full border px-3 py-1.5 text-sm transition-colors',
                      selected
                        ? 'border-brand-500 bg-brand-50 text-brand-500'
                        : 'border-slateish-300 text-slateish-600 hover:bg-slateish-50'
                    )}
                  >
                    {court.name}
                  </button>
                )
              })}
            </div>
          ) : null}
          <FieldError message={errors.courts} />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="closure-reason">
            사유{reasonRequired ? '' : ' (선택)'}
          </label>
          <Textarea
            id="closure-reason"
            rows={2}
            value={form.reason}
            placeholder="내부 확인용이며 사용자에게는 표시되지 않습니다."
            aria-invalid={Boolean(errors.reason)}
            onChange={(event) => {
              updateForm('reason', event.target.value)
            }}
          />
          <FieldError message={errors.reason} />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button type="button" onClick={handleSubmit}>
            다음
          </Button>
        </div>
      </div>
    </Modal>
  )
}
